import React, { useEffect, useMemo } from 'react';
import format from 'date-fns/format';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import PageTitle from 'components/PageTitle';
import { zodResolver } from '@hookform/resolvers/zod';
import { GraniteInput } from '../../components/V2/Input/GraniteInput';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { BadgeRole } from 'components/V2/BadgeRole/BadgeRole';
import DeleteAccount from './DeleteAccount';
import { clsx } from 'clsx';
import {
  EditUserForm,
  EditUserFormSchema,
  responseToUserEditForm,
  userEditFormToEditRequest,
} from './form-schema';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getUserByEmail, removeUserFromGroup, updateUser } from 'api/users/api';
import showToast from 'components/Toast/Toast';
import { WithLoader } from '../../components/WithLoader/WithLoader';
import { UserRolePermissionFeatureSwitches } from '../../components/UserPermissionFeatureSwitches/UserRolePermissionFeatureSwitches';
import Divider from '../../components/Divider';
import { useAuthUser } from '../../hooks/useAuthUser';
import { GraniteSelect } from '../../components/Select/Select';
import {
  UpdateUserProfileRequest,
  UserProfileResponse,
} from '../../api/users/schemas/UserProfile';
import PhoneNumberInput from 'components/V2/Input/PhoneNumber';
import { TokenUserPermissions, TokenUserRoles } from 'api/users/schemas/Users';
import { usePermissions } from 'hooks/usePermissions';
import UserAccounts, {
  accountOptions,
} from 'screens/UserAccounts/UserAccounts';
import {
  getDisplayStatus,
  indicatorBgColor,
} from 'components/UserStatusColumn';
import Loader from 'components/Loader/Loader';
import { DisplayForPermission } from 'components/Permission/DisplayForPermission';

export const EditUser = () => {
  const { id: email } = useParams<{ id: string }>();
  const { companyName, grandparentMacnum } = useAuthUser();
  const { isSuperAdmin } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    ['edit-user', email],
    () => getUserByEmail({ email: email }),
    {
      refetchOnWindowFocus: false,
      enabled: !!email,
    },
  );

  const {
    handleSubmit,
    control,
    reset,
    register,
    formState: { errors },
  } = useForm<EditUserForm>({
    resolver: zodResolver(EditUserFormSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (data) {
      reset({
        ...responseToUserEditForm(data),
        account:
          data?.sub_groups?.length === 0
            ? accountOptions[1]
            : accountOptions[0],
        sub_groups: data.roles.some((role) =>
          role.value.includes(TokenUserRoles.COMPANY_ADMIN),
        )
          ? []
          : data.sub_groups?.map((subGroup) => subGroup.profile.cw_id) || [],
      });
    }
  }, [data, reset]);

  const systemRole = useMemo(() => {
    return data?.roles.find((role) =>
      [
        TokenUserRoles.COMPANY_ADMIN,
        TokenUserRoles.USER,
        TokenUserRoles.VIEWER,
        TokenUserRoles.WELCOME_USER,
      ].includes(role.value as TokenUserRoles),
    );
  }, [data?.roles]);

  const isSystemSuperAdmin = useMemo(() => {
    return data?.roles.find(
      (role) => role.value === TokenUserRoles['SUPER_ADMIN'],
    );
  }, [data?.roles]);

  const breadcrumbs = [
    {
      icon: 'home',
      label: 'User management',
      onClick: () => navigate('/users/'),
    },
  ];

  const updateUserRoleMutation = useMutation(
    (payload: UpdateUserProfileRequest) => updateUser(email, payload),
    {
      onSuccess: (response: UserProfileResponse) => {
        showToast.confirmation({
          message: 'User role has been successfully updated!',
        });
        reset({
          ...responseToUserEditForm(response),
          account:
            response?.sub_groups?.length === 0
              ? accountOptions[1]
              : accountOptions[0],
          sub_groups: response.roles.some((role) =>
            role.value.includes(TokenUserRoles.COMPANY_ADMIN),
          )
            ? []
            : response.sub_groups?.map((subGroup) => subGroup.profile.cw_id) ||
              [],
        });
        navigate(`/users/${email}`);
      },
      onError(error) {
        console.log(error, 'error');
      },
    },
  );

  const { mutate, isLoading: isRemovingFromGroup } = useMutation(
    ({ userId }: { userId: string }) => removeUserFromGroup({ userId }),
    {
      onSuccess: (data: { message: string }) => {
        showToast.confirmation({
          message: data.message,
        });
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'users' &&
            typeof query.queryKey[1] === 'object',
        });
        navigate(`/users`);
      },
      onError: (error: { message: string }) => {
        showToast.error({
          message: error.message || 'Something went wrong!',
        });
      },
    },
  );

  if (!email) {
    navigate('/users');
    return;
  }

  const onSubmit = (form: EditUserForm) => {
    if (!data) return;
    updateUserRoleMutation.mutate({
      ...userEditFormToEditRequest(form, data),
      ...{ sub_groups: form.sub_groups || [] },
    });
  };

  const onError = (err: unknown) => {
    console.log({ err });
  };

  const dateAdded = data?.invited_at || data?.created_at;

  return (
    <div className="w-full p-8">
      <WithLoader isLoading={isLoading}>
        <div className="mx-auto flex w-full flex-col items-start justify-start">
          <PageTitle
            title={data?.name ?? ''}
            breadcrumbs={breadcrumbs}
            className="mb-12 w-full"
          >
            <div className="ml-6 flex grow items-end">
              <BadgeRole
                role={
                  isSystemSuperAdmin
                    ? TokenUserRoles['SUPER_ADMIN']
                    : systemRole?.value ?? ''
                }
                classNames="text-base py-1.5 h-[36px] px-3 ml-0"
              />
              <GraniteButton
                variant="secondary"
                className="button large secondary ml-auto mr-4"
                size="large"
                onClick={() => navigate(`/users/${email}`)}
              >
                Cancel
              </GraniteButton>
              <GraniteButton
                variant="primary"
                size="large"
                type="submit"
                form="invite-member-form"
                disabled={updateUserRoleMutation.isLoading}
                className="mr-0"
              >
                Save changes
              </GraniteButton>
            </div>
          </PageTitle>

          <div className="flex w-full flex-col items-start justify-start gap-6">
            <div className="flex w-full items-center justify-between rounded bg-background-base-surface-2 px-6 py-4">
              <div className="flex w-full items-center justify-start gap-3">
                <span
                  className={clsx(
                    `h-4 w-4 rounded-full`,
                    indicatorBgColor(data?.global_status ?? ''),
                  )}
                ></span>
                <h2 className="flex gap-6 text-xl font-bold text-content-base-default">
                  {getDisplayStatus(data?.global_status ?? '')}
                </h2>
              </div>
              <div className="flex w-full flex-col items-end justify-start">
                <p className="text-base font-bold text-content-base-subdued">
                  Date Added
                </p>
                <h2 className="text-base font-bold text-content-base-default">
                  {dateAdded
                    ? format(new Date(dateAdded), 'MM/dd/yyyy')
                    : ' -- '}
                </h2>
              </div>
            </div>
            <form
              onSubmit={handleSubmit(onSubmit, onError)}
              className="self-stretch"
              id="invite-member-form"
            >
              <div className="grid h-full w-full grid-cols-12 overflow-hidden rounded">
                <div className="col-span-4 flex flex-col justify-between rounded-l bg-background-base-surface-3 px-8 py-6">
                  <h2 className="mb-4 text-2xl font-bold text-content-base-default">
                    User details
                  </h2>
                  <div className="flex flex-col gap-6">
                    <GraniteInput
                      label="First Name"
                      placeholder="First Name"
                      {...register('first_name')}
                      error={errors.first_name?.message}
                    />
                    <GraniteInput
                      label="Last Name"
                      placeholder="Last Name"
                      {...register('last_name')}
                      error={errors.last_name?.message}
                    />
                    <GraniteInput
                      label="Email"
                      placeholder="Email"
                      value={email}
                      disabled
                    />
                    <PhoneNumberInput
                      name="phone_number"
                      label="Phone"
                      placeholder="(555)-555-555"
                      control={control}
                      error={errors.phone_number?.message}
                    />
                    <GraniteSelect
                      label="Company"
                      value={{ label: companyName, value: companyName }}
                      options={[{ label: companyName, value: companyName }]}
                      isDisabled
                    />
                    <GraniteInput
                      label="Company account #"
                      value={grandparentMacnum}
                      disabled
                    />
                  </div>
                  <div className="mt-11 flex w-full justify-between gap-4">
                    <DisplayForPermission
                      permission={TokenUserPermissions.GRANITE_INTERNAL}
                    >
                      <GraniteButton
                        variant="secondary"
                        size="large"
                        className={clsx(
                          isLoading && 'loading-button',
                          '!flex-1',
                        )}
                        onClick={() => mutate({ userId: data?.id ?? '' })}
                      >
                        Remove from company
                        {isRemovingFromGroup && (
                          <Loader animationClassname="!w-3 !h-3" />
                        )}
                      </GraniteButton>
                    </DisplayForPermission>
                    <DeleteAccount
                      onlyButton
                      userId={data?.id ?? ''}
                      buttonWrapperClassName={clsx(
                        !TokenUserPermissions.GRANITE_INTERNAL && 'w-full',
                      )}
                      buttonLabel={
                        !TokenUserPermissions.GRANITE_INTERNAL
                          ? 'Delete user'
                          : undefined
                      }
                      onSuccessHandler={() => {
                        showToast.confirmation({
                          message: 'User has been successfully deleted!',
                        });
                        navigate('/users');
                      }}
                    />
                  </div>
                </div>
                <div className="col-span-8 flex h-full w-full flex-col gap-8 rounded-r bg-background-base-surface-2 px-8 py-6">
                  <UserRolePermissionFeatureSwitches
                    control={control}
                    isSuperAdmin={isSuperAdmin}
                  />
                  <UserAccounts control={control} />
                  <Divider />
                  <div className="mt-4 flex flex-1 items-end justify-between gap-4">
                    <div className="flex gap-4">
                      <GraniteButton
                        variant="secondary"
                        className="button large secondary ml-auto"
                        size="large"
                        onClick={() => navigate(`/users/${email}`)}
                      >
                        Cancel
                      </GraniteButton>
                      <GraniteButton
                        variant="primary"
                        className="whitespace-nowrap"
                        size="large"
                        type="submit"
                        form="invite-member-form"
                        disabled={updateUserRoleMutation.isLoading}
                      >
                        Save changes
                      </GraniteButton>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </WithLoader>
    </div>
  );
};
